import { useRef } from "react";
import { Container } from "react-bootstrap";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import {
  Basket2Fill,
  BoxSeamFill,
  Disc,
  Droplet,
  HourglassSplit,
  PatchCheckFill,
} from "react-bootstrap-icons";
import SectionHeading from "../ui/SectionHeading";
import "../style/CraftProcess.css";

const STEPS = [
  {
    icon: Basket2Fill,
    image: "/images/riceraw2.jpg",
    title: "Sourced with Care",
    text: "Rice and urad dal sourced from trusted local farms — nothing else goes in.",
  },
  {
    icon: Droplet,
    image: "/images/urad2.jpg",
    title: "Soaked Slow",
    text: "Grains soak for hours the old-fashioned way, so nothing has to be rushed.",
  },
  {
    icon: Disc,
    image: "/images/wetgrinder.png",
    title: "Stone-Ground",
    text: "Single stone grinders turn slowly, preserving a texture steel blades can't match.",
  },
  {
    icon: HourglassSplit,
    image: "/images/naturalnobackgrnd.png",
    title: "Fermented Naturally",
    text: "Time and temperature do the work — no additives, just patience.",
  },
  {
    icon: PatchCheckFill,
    image: "/images/icons/quality.png",
    title: "Quality Checked",
    text: "Every batch is checked for consistency, taste and freshness before it leaves.",
  },
  {
    icon: BoxSeamFill,
    image: "/images/BB_idli_1x1_instagram.jpg",
    title: "Packed Fresh",
    text: "Sealed in food-grade, ziplock pouches and out the door the same day.",
  },
];

const stepVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 260, damping: 24 },
  },
};

const CraftProcess = () => {
  const trackRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start 0.8", "end 0.4"],
  });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section className="section-pad craft-process">
      <Container className="container-narrow">
        <SectionHeading
          eyebrow="How It's Made"
          title="The Art of Crafting the Perfect Batter"
          subtitle="Traditional methods, modern hygiene — every step protects the taste that earned people's trust in the first place."
        />

        <div className="craft-process__track" ref={trackRef}>
          <div className="craft-process__line-rail" aria-hidden="true">
            <motion.div
              className="craft-process__line-fill"
              style={{ scaleY: shouldReduceMotion ? 1 : lineScale }}
            />
          </div>

          <ul className="craft-process__list">
            {STEPS.map(({ icon: Icon, image, title, text }, i) => (
              <motion.li
                key={title}
                className={`craft-process__step ${
                  i % 2 === 0 ? "craft-process__step--left" : "craft-process__step--right"
                }`}
                variants={stepVariants}
                initial={shouldReduceMotion ? "visible" : "hidden"}
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: shouldReduceMotion ? 0 : (i % 2) * 0.08 }}
              >
                <div className="craft-process__card card-surface">
                  <span className="craft-process__step-no">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="craft-process__icon-wrap">
                    <Icon size={20} />
                  </div>
                  <div className="craft-process__media">
                    <img src={image} alt={title} loading="lazy" />
                  </div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  );
};

export default CraftProcess;
